import React, { useCallback, useMemo, useState } from 'react'
import clsx from 'clsx'
import { Tabs } from 'antd'

import QuestionsTab from './ContentTabs/QuestionsTab'
import SuggestionsTab from './ContentTabs/SuggestionsTab'
import KeywordsTab from './ContentTabs/KeywordsTab'
import SEOPaneLoader from './SEOPaneLoader'
import SEOPaneEmpty from './SEOPaneEmpty'

import {
  useContentResearchReport,
  useContentResearchReportQuestions,
  useContentResearchReportSuggestions,
  useContentResearchReportKeywords
} from '../../hooks'

import styles from './SEOPane.module.css'

const { TabPane } = Tabs

interface Props {
  className?: string
  contentId: string
  companyId: string
  editorContentText: string
  clientKeywords?: string[]
  sort_by?: string | null
  platformName: 'operations' | 'business' | 'creators'
}

type TabKeys = 'keywords' | 'questions' | 'suggestions'

export function SEOPaneTabContainer({
  className,
  companyId,
  contentId,
  editorContentText,
  clientKeywords,
  sort_by,
  platformName
}: Props): JSX.Element {
  const [activeTab, setActiveTab] = useState<TabKeys>('keywords')

  const { reportDetails, isLoading: isReportLoading } =
    useContentResearchReport(companyId, contentId)

  const { reportQuestions } = useContentResearchReportQuestions(
    companyId,
    contentId
  )

  const keywordsApiParams = ['client_provided', 'unused'].includes(
    sort_by ?? ''
  )
    ? {}
    : {
        sort_by: sort_by || 'importance'
      }
  const { reportKeywords } = useContentResearchReportKeywords(
    companyId,
    contentId,
    keywordsApiParams
  )

  const { reportSuggestions } = useContentResearchReportSuggestions(
    companyId,
    contentId
  )

  const allKeywords = useMemo(() => {
    const clientKeywordsSet = new Set(clientKeywords)
    const enhancedReportKeywords: Optional<ReportKeywords, 'term'>[] =
      reportKeywords.map(keyword => {
        const isUsedInDoc = RegExp(keyword.term, 'gi').test(editorContentText)
        if (clientKeywordsSet.has(keyword.term)) {
          clientKeywordsSet.delete(keyword.term)
          return { ...keyword, isProvidedByClient: true, isUsedInDoc }
        }
        return { ...keyword, isUsedInDoc }
      })
    clientKeywordsSet.forEach(keyword => {
      const isUsedInDoc = RegExp(keyword, 'gi').test(editorContentText)
      enhancedReportKeywords.push({
        term: keyword,
        isProvidedByClient: true,
        isUsedInDoc
      })
    })

    enhancedReportKeywords.sort((firstKeyword, secondKeyword) => {
      if (sort_by === 'unused') {
        if (!firstKeyword.isUsedInDoc && !secondKeyword.isUsedInDoc) return 0
        if (!firstKeyword.isUsedInDoc) return -1
        return 1
      }
      if (sort_by === 'client_provided') {
        if (firstKeyword.isProvidedByClient && secondKeyword.isProvidedByClient)
          return 0
        if (firstKeyword.isProvidedByClient) return -1
        return 1
      }
      return 0
    })

    return [...enhancedReportKeywords]
  }, [clientKeywords, reportKeywords, editorContentText, sort_by])

  const getTabContent = useCallback(
    (tab: TabKeys) => {
      if (isReportLoading) return <SEOPaneLoader />
      if (!reportDetails) return <SEOPaneEmpty />
      if (reportDetails.status === 'pending') return <SEOPaneLoader />
      if (reportDetails.status === 'completed') {
        switch (tab) {
          case 'keywords': {
            return (
              <KeywordsTab
                reportKeywords={allKeywords}
                platformName={platformName}
              />
            )
          }
          case 'questions': {
            return <QuestionsTab reportQuestions={reportQuestions} />
          }
          case 'suggestions': {
            return <SuggestionsTab reportSuggestions={reportSuggestions} />
          }
          default: {
            return null
          }
        }
      }

      return <SEOPaneEmpty />
    },
    [
      isReportLoading,
      reportDetails,
      allKeywords,
      platformName,
      reportQuestions,
      reportSuggestions
    ]
  )

  return (
    <Tabs
      onTabClick={_activeTab => setActiveTab(_activeTab as TabKeys)}
      activeKey={activeTab}
      className={clsx(className, styles.tabWrapper)}
      tabBarGutter={20}
    >
      <TabPane
        tab={<div className={styles.tabHeader}>Keywords</div>}
        key="keywords"
        className={styles.tabPane}
      >
        {getTabContent('keywords')}
      </TabPane>
      <TabPane
        tab={<div className={styles.tabHeader}>Questions</div>}
        key="questions"
        className={styles.tabPane}
      >
        {getTabContent('questions')}
      </TabPane>
      <TabPane
        tab={<div className={styles.tabHeader}>Outline suggestions</div>}
        key="suggestions"
        className={styles.tabPane}
      >
        {getTabContent('suggestions')}
      </TabPane>
    </Tabs>
  )
}
